import { useState, type FormEvent } from 'react';
import { SiteIdentity } from '../components/SiteIdentity';
import { api, ApiError } from '../api';
import { GoogleButton } from '../components/GoogleButton';
import type { User } from '../types';

interface Props {
  onDone: (user: User) => void;
  onSwitch: () => void;
}

function messageFor(code: string): string {
  switch (code) {
    case 'email_taken':
      return 'Bu e-poçt artıq qeydiyyatdadır. Giriş etməyi sınayın.';
    case 'invalid_email':
      return 'E-poçt ünvanı düzgün deyil.';
    case 'weak_password':
      return 'Şifrə ən azı 10 simvol olmalıdır.';
    case 'company_required':
      return 'Şirkət adını yazın.';
    case 'disposable_email':
      return 'Birdəfəlik e-poçt xidmətləri qəbul edilmir. İş ünvanınızı yazın.';
    case 'missing_fields':
      return 'Bütün məcburi sahələri doldurun.';
    default:
      return 'Qeydiyyat alınmadı. Bir azdan yenidən yoxlayın.';
  }
}

export function Register({ onDone, onSwitch }: Props): React.ReactElement {
  const [company, setCompany] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const tooShort = password.length > 0 && password.length < 10;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.register({
        email,
        password,
        company,
        ...(fullName.trim() ? { fullName } : {}),
        marketingOptIn: marketing,
      });
      onDone(user);
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <span className="login-mark">M</span>
          <span className="login-name">Mailora</span>
        </div>
        {/*
          ⚠️ «PULSUZ» VƏDİ YOXDUR — QƏSDƏN (2026-08-08 tarifi).
          Əvvəl burada «14 gün pulsuz» yazılırdı. Sınaq ləğv ediləndən
          sonra o cümlə YALAN olardı: hesab yaradılır, amma poçt ödənişə
          qədər işləmir. Qeydiyyat ekranında verilməyən vədi sonra izah
          etməkdənsə, qiyməti elə burada demək daha ucuz başa gəlir —
          bu mərhələdə gedən adam onsuz da ödəməyəcəkdi.
        */}
        <p className="login-lead">Şirkət hesabı yaradın — domen başına 5 AZN / ay</p>

        <label className="field">
          <span>Şirkət adı</span>
          <input
            required
            autoFocus
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            // Nümunə ad ÜMUMİ olmalıdır — əvvəl burada real müştərinin adı
            // yazılmışdı və hər yeni müştəri onu görürdü.
            placeholder="Şirkətiniz MMC"
          />
        </label>

        <label className="field">
          <span>Ad, soyad</span>
          <input
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="İstəyə bağlı"
          />
        </label>

        <label className="field">
          <span>E-poçt ünvanınız</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="siz@example.com"
          />
          {/* Qeydiyyat anında müştərinin hələ domeni yoxdur — mövcud
              ünvanı ilə hesab açır, domeni sonra bağlayır. */}
          <small className="field-hint">
            Mövcud ünvanınızı yazın. Şirkət poçtunuzu sonra yaradacaqsınız.
          </small>
        </label>

        <label className="field">
          <span>Şifrə</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <small className={`field-hint ${tooShort ? 'is-warn' : ''}`}>
            Ən azı 10 simvol
          </small>
        </label>

        {/*
          MARKETİNQ RAZILIĞI — AYRICA VƏ ÖNCƏDƏN İŞARƏSİZ.
          ⚠️ Şərtlərlə BİRLƏŞDİRİLƏ BİLMƏZ. «Şərtləri qəbul edirəm və
          reklam almağa razıyam» şəklində bir qutu hər iki hüquq
          sistemində etibarsız razılıqdır, çünki istifadəçi xidmətdən
          imtina etmədən reklamdan imtina edə bilmir.
          ⚠️ `defaultChecked` YAZMA — öncədən işarələnmiş qutu razılıq deyil.
        */}
        <label className="check">
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
          />
          <span>Yeniliklər və təkliflər haqqında məktub almaq istəyirəm</span>
        </label>

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Yaradılır…' : 'Hesab yarat'}
        </button>

        {/*
          ŞƏRTLƏR — QUTU YOX, DÜYMƏNİN ÜSTÜNDƏ MƏTN.
          Şərtlərə razılıq müqavilə zərurətidir, GDPR mənasında «razılıq»
          deyil — ona görə ayrıca qutu tələb olunmur və funnel-ə əlavə
          maneə qoymur. Amma razılıq faktı BAZAYA YAZILIR (consents
          cədvəli): göstərdiyimiz mətnin hansı versiya olduğu, nə vaxt və
          hansı IP-dən qəbul edildiyi qeyd olunur. Bax: migrasiya 009.
        */}
        <p className="login-terms">
          Hesab yaratmaqla{' '}
          <a href="https://mailora.az/sertler" target="_blank" rel="noreferrer">
            İstifadə şərtləri
          </a>{' '}
          və{' '}
          <a href="https://mailora.az/mexfilik" target="_blank" rel="noreferrer">
            Məxfilik siyasəti
          </a>{' '}
          ilə razılaşırsınız.
        </p>

        {/* Google ilə gələn e-poçt onsuz da təsdiqlənmiş sayılır — ayrıca
            təsdiq məktubuna ehtiyac qalmır (docs/08, 3-cü boşluq). */}
        <GoogleButton />

        <p className="login-alt">
          Artıq hesabınız var?{' '}
          <button type="button" className="linkish" onClick={onSwitch}>
            Daxil olun
          </button>
        </p>

        {/* Qeydiyyat da kimlik məlumatı toplayır — eyni siqnal lazımdır. */}
        <SiteIdentity />
      </form>
    </div>
  );
}
